import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "fileInput", "textInput", "summary", "modalTable", "modalRows"]

  pickText(event) {
    event?.preventDefault?.()
    if (!this.hasTextInputTarget) return
    const lines = this.textInputTarget.value
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
    if (lines.length === 0) return

    lines.forEach((label) => this.appendRow({ label }))

    this.textInputTarget.value = ""
    this.refreshSummary()
  }

  // Add a single empty plate row — used by the CREATION-mode "+ Add plate"
  // button. Focuses the new plate-name input so the operator can start typing.
  add(event) {
    event?.preventDefault?.()
    const row = this.appendRow({})
    if (row) {
      const labelInput = row.querySelector("input[name$='[label]']")
      if (labelInput) labelInput.focus()
    }
    this.refreshSummary()
  }

  // ─── BULK-ADD MODAL ───────────────────────────────────────────────────────
  // The CREATION-mode "+ Add plates" button opens a Bootstrap modal with an
  // editable table. Paste-from-Google-Sheets lands as a tab-separated multi-
  // line string in the clipboard; we detect TSV and auto-split into rows.

  openPasteModal(event) {
    // Reset rows + seed with 3 blank rows so paste has somewhere to land.
    if (!this.hasModalRowsTarget) return
    this.modalRowsTarget.innerHTML = ""
    for (let i = 0; i < 3; i++) this.addModalRow()
  }

  addModalRow(event) {
    event?.preventDefault?.()
    if (!this.hasModalRowsTarget) return
    const idx = this.modalRowsTarget.children.length + 1
    const tr  = document.createElement("tr")
    tr.innerHTML = `
      <td class="text-muted small" style="font-size:0.7rem;">${idx}</td>
      <td><input type="text" class="form-control form-control-sm" data-modal-col="label" placeholder="e.g. Margherita" style="font-size:0.75rem;"></td>
      <td><input type="text" class="form-control form-control-sm" data-modal-col="plate_id" placeholder="SKU-042" style="font-size:0.75rem;"></td>
      <td><textarea rows="1" class="form-control form-control-sm" data-modal-col="plate_description" placeholder="Ingredients · garnish · sauce · plating cues" style="font-size:0.75rem; resize:vertical;"></textarea></td>
      <td class="text-end"><button type="button" class="btn btn-sm btn-link text-danger p-0" style="font-size:0.9rem;" data-action="click->food-references#removeModalRow"><i class="fa-solid fa-xmark"></i></button></td>
    `
    // Attach paste handler to every input — TSV paste auto-fills.
    tr.querySelectorAll("input, textarea").forEach((el) => {
      el.addEventListener("paste", (e) => this.handleModalPaste(e))
    })
    this.modalRowsTarget.appendChild(tr)
    return tr
  }

  removeModalRow(event) {
    event?.preventDefault?.()
    const tr = event.currentTarget.closest("tr")
    if (!tr) return
    tr.remove()
    this.renumberModalRows()
  }

  renumberModalRows() {
    if (!this.hasModalRowsTarget) return
    Array.from(this.modalRowsTarget.children).forEach((tr, i) => {
      const cell = tr.firstElementChild
      if (cell) cell.textContent = String(i + 1)
    })
  }

  // Paste handler: if clipboard contains a tab character, treat it as TSV
  // (Google Sheets / Excel) and auto-fill cells starting from the cell that
  // received the paste.
  handleModalPaste(event) {
    const text = event.clipboardData?.getData("text/plain") || ""
    if (!text.includes("\t") && !text.includes("\n")) return // single value — let default paste behaviour fire

    event.preventDefault()
    const lines = text.split(/\r?\n/).filter((l) => l.length > 0)
    if (!lines.length) return

    const startCell = event.currentTarget
    const startTr   = startCell.closest("tr")
    const startCol  = startCell.dataset.modalCol
    const colOrder  = ["label", "plate_id", "plate_description"]
    const startColIdx = colOrder.indexOf(startCol)
    if (startColIdx === -1) return

    let currentTr = startTr
    lines.forEach((line) => {
      const cells = line.split("\t")
      // Ensure we have enough rows.
      if (!currentTr) currentTr = this.addModalRow()
      cells.forEach((value, i) => {
        const colName = colOrder[startColIdx + i]
        if (!colName) return
        const inputEl = currentTr.querySelector(`[data-modal-col="${colName}"]`)
        if (inputEl) inputEl.value = value.trim()
      })
      currentTr = currentTr.nextElementSibling
    })
  }

  // Save: iterate modal rows, push every non-empty one to the real
  // food_references container, then close the modal.
  confirmModalRows(event) {
    if (!this.hasModalRowsTarget) return
    const rows = Array.from(this.modalRowsTarget.children)
    let added  = 0
    rows.forEach((tr) => {
      const label       = tr.querySelector('[data-modal-col="label"]')?.value?.trim() || ""
      const plate_id    = tr.querySelector('[data-modal-col="plate_id"]')?.value?.trim() || ""
      const plate_description = tr.querySelector('[data-modal-col="plate_description"]')?.value?.trim() || ""
      // Skip rows where everything is blank.
      if (!label && !plate_id && !plate_description) return
      this.appendRow({ label, plate_id, plate_description })
      added += 1
    })
    if (added === 0) return
    this.refreshSummary()
  }

  pickFolder(event) {
    const input = event.currentTarget
    const files = Array.from(input.files || []).filter(this.isImage)
    if (!files.length) return

    files.sort((a, b) => {
      const ap = a.webkitRelativePath || a.name
      const bp = b.webkitRelativePath || b.name
      return ap.localeCompare(bp, undefined, { numeric: true, sensitivity: "base" })
    })

    files.forEach((file) => this.appendRow({ file, label: this.deriveLabel(file) }))

    if (this.hasSummaryTarget) {
      const total = this.containerTarget.children.length
      this.summaryTarget.textContent = `${total} reference${total === 1 ? "" : "s"} attached. Pick one as the hero — its background will be replicated on the new dish.`
    }

    input.value = ""
  }

  appendRow({ file = null, label = "", plate_id = "", plate_description = "" } = {}) {
    const index = Date.now() + Math.floor(Math.random() * 1000)
    const html = this.templateTarget.innerHTML.replace(/__INDEX__/g, index)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const row = wrapper.firstElementChild
    this.containerTarget.appendChild(row)

    // Auto-check `selected: true` on every newly-added row — adding a row
    // implies "include in the fanout". Without this, food_fanout! sees an
    // empty `where(selected: true)` and the AI would only render the root.
    const selectedCheckbox = row.querySelector("input[type='checkbox'][name$='[selected]']")
    if (selectedCheckbox) selectedCheckbox.checked = true

    if (label) {
      const labelInput = row.querySelector("input[name$='[label]']")
      if (labelInput) labelInput.value = label
    }
    if (plate_id) {
      const idInput = row.querySelector("input[name$='[plate_id]']")
      if (idInput) idInput.value = plate_id
    }
    if (plate_description) {
      const descInput = row.querySelector("textarea[name$='[plate_description]']")
      if (descInput) descInput.value = plate_description
    }
    if (file) {
      const fileInput = row.querySelector("input[type='file']")
      const dt = new DataTransfer()
      dt.items.add(file)
      fileInput.files = dt.files

      const thumb = row.querySelector("[data-food-references-target='thumb']") || row.querySelector("img.thumb")
      if (thumb) {
        const url = URL.createObjectURL(file)
        thumb.src = url
        thumb.hidden = false
      }
    }

    // Tell the industry-toggle controller to re-run its food-mode visibility
    // pass so the brand-new row's enhancing-only / creation-only blocks show
    // or hide according to the currently-selected mode.
    window.dispatchEvent(new CustomEvent("food-row-added", { bubbles: true }))
    return row
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const destroy = row.querySelector("input[name$='[_destroy]']")
    if (destroy) {
      destroy.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.refreshSummary()
  }

  anchorChanged(event) {
    if (!event.target.checked) return
    const radios = this.element.querySelectorAll("input[type='checkbox'][name$='[style_anchor]']")
    radios.forEach((r) => {
      if (r !== event.target) r.checked = false
    })
  }

  isImage(file) {
    return /^image\/(jpeg|png|webp)$/.test(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name)
  }

  deriveLabel(file) {
    const name = file.name.replace(/\.[^.]+$/, "")
    return name.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  }

  refreshSummary() {
    if (!this.hasSummaryTarget) return
    const visible = Array.from(this.containerTarget.children).filter((r) => r.style.display !== "none").length
    this.summaryTarget.textContent = visible === 0
      ? "Upload a folder or pick files to start."
      : `${visible} reference${visible === 1 ? "" : "s"} attached. Pick one as the hero — its background will be replicated.`
  }
}
